import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"

// The backend owns the cap (NaturaApiService::DEPOSIT_TRANSFER_MAX_LOTS) and
// reports it through `capped` and `limit`. A client-side count check drifted out
// of sync with it once and blocked orders the backend accepted, so this value is
// only the cap-message fallback when a response omits `limit`.
const LOT_CAP = 1000

export default function PreviewTable({
  preview,
  finalize,
  onFinalizeChange,
  onConfirm,
  onCancel,
  confirming,
  submitted,
}) {
  const { t } = useTranslation()

  const tuples = preview.tuples || []
  const skipped = preview.skipped || []
  const summary = preview.summary || {}
  const lotCount = summary.lot_count ?? tuples.length
  const statuses = preview.resolved?.context?.statuses || []

  const overCap = preview.capped === true
  const empty = lotCount === 0
  // `submitted`: the batch already went to SAP, so this preview must not fire twice.
  const confirmDisabled = confirming || submitted || overCap || empty

  // Non-REM modes (store_location, manual) DISCARD the moved lost units on
  // finalize; REM ships the order/units and registers billing. Warn accordingly.
  const finalizeWarningKey =
    preview.mode === "rem_order"
      ? "natura_operations.deposit_transfer.finalize_warning"
      : "natura_operations.deposit_transfer.finalize_warning_discard"

  return (
    <div className="mt-6" data-testid="deposit-transfer-preview">
      <h2 className="font-semibold mb-2">{t("natura_operations.deposit_transfer.step3_heading")}</h2>

      <div className="mb-2 text-sm text-gray-700" data-testid="dt-preview-summary">
        {t("natura_operations.deposit_transfer.preview_summary", {
          lot_count: lotCount,
          total_units: summary.total_units ?? 0,
          skipped_units: summary.skipped_units ?? 0,
        })}
      </div>

      {statuses.length > 0 && (
        <div className="mb-2 text-xs text-gray-500">
          {t("natura_operations.deposit_transfer.preview_statuses", { statuses: statuses.join(", ") })}
        </div>
      )}

      {skipped.length > 0 && (
        <div role="alert" className="mb-3 p-3 border border-amber-200 bg-amber-50 rounded text-sm text-amber-800">
          <div className="font-medium mb-1">{t("natura_operations.deposit_transfer.skipped_heading")}</div>
          <ul className="list-disc pl-5 space-y-0.5">
            {skipped.map((s, i) => (
              <li key={i}>
                {s.reason === "no_lot"
                  ? t("natura_operations.deposit_transfer.skipped_no_lot", { quantity: s.quantity ?? 0, detail: s.detail || "" })
                  : t("natura_operations.deposit_transfer.skipped_generic", { reason: s.reason || "", quantity: s.quantity ?? 0, detail: s.detail || "" })}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">{t("natura_operations.deposit_transfer.col_sku")}</th>
              <th className="px-3 py-2 font-medium">{t("natura_operations.deposit_transfer.col_lot")}</th>
              <th className="px-3 py-2 font-medium">{t("natura_operations.deposit_transfer.col_quantity")}</th>
              <th className="px-3 py-2 font-medium">{t("natura_operations.deposit_transfer.col_source_location")}</th>
            </tr>
          </thead>
          <tbody>
            {tuples.map((row, i) => (
              <tr key={row.lot_id ?? i} className="border-t border-gray-100" data-testid={`dt-preview-row-${row.lot_id ?? i}`}>
                <td className="px-3 py-2 font-mono">{row.sku ?? "—"}</td>
                <td className="px-3 py-2 font-mono">{row.lot_code ?? "—"}</td>
                <td className="px-3 py-2">{row.quantity ?? 0}</td>
                <td className="px-3 py-2">{row.source_location ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {overCap && (
        <div role="alert" className="mt-3 p-3 border border-red-200 bg-red-50 rounded text-sm text-red-800">
          {t("natura_operations.deposit_transfer.cap_message", { limit: preview.limit ?? LOT_CAP, given: lotCount })}
        </div>
      )}

      {empty && !overCap && (
        <div className="mt-3 p-3 border border-gray-200 bg-gray-50 rounded text-sm text-gray-700">
          {t("natura_operations.deposit_transfer.empty_message")}
        </div>
      )}

      <div className="mt-4">
        <label className="inline-flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            aria-label="finalize"
            checked={finalize}
            disabled={confirming}
            onChange={(e) => onFinalizeChange(e.target.checked)}
            className="mt-0.5 text-amber-500 focus:ring-amber-500"
          />
          <span>
            <span className="font-medium">{t("natura_operations.deposit_transfer.finalize_checkbox")}</span>
            <span className="block text-xs text-amber-700">{t(finalizeWarningKey)}</span>
          </span>
        </label>
      </div>

      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={onConfirm}
          disabled={confirmDisabled}
          className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white"
        >
          {confirming
            ? t("natura_operations.deposit_transfer.confirming")
            : t("natura_operations.deposit_transfer.confirm")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={confirming}
          className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
        >
          {t("natura_operations.deposit_transfer.cancel")}
        </button>
      </div>
    </div>
  )
}

PreviewTable.propTypes = {
  preview: PropTypes.object.isRequired,
  finalize: PropTypes.bool.isRequired,
  onFinalizeChange: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  confirming: PropTypes.bool,
  submitted: PropTypes.bool,
}

PreviewTable.defaultProps = {
  confirming: false,
  submitted: false,
}
